import type { FileDto } from '@/api/api.dto';
import type { MediaStatus, MediaVariantDto } from '@/common/media';
import { parseStops } from '@/utils/eventStops';

import type { TagDto } from '../tags/tags.dto';
import type { TaskResponse } from '../tasks/tasks.dto';

export type UUID = string;
export type ISODateString = string;

export enum EVENTS {
  'OUT_OF_WATER' = 'out_of_water',
  'GENERAL' = 'general',
  'GUEST' = 'guest',
  'FOOD' = 'food',
  'VOYAGE' = 'voyage',
  'TRIP' = 'trip',
}

export const EVENT_TYPE_COLORS: Record<EVENTS, string> = {
  [EVENTS.OUT_OF_WATER]: '#F59E0B',
  [EVENTS.GENERAL]: '#38bdf8',
  [EVENTS.GUEST]: '#4ECDC4',
  [EVENTS.FOOD]: '#FF6B6B',
  [EVENTS.VOYAGE]: '#A78BFA',
  [EVENTS.TRIP]: '#A78BFA',
};

export const EVENT_TYPES: ReadonlyArray<{
  readonly id: EVENTS;
  readonly label: string;
  readonly emoji?: string;
}> = [
  { id: EVENTS.OUT_OF_WATER, label: 'Out of Water' },
  { id: EVENTS.GENERAL, label: 'General', emoji: '📅' },
  { id: EVENTS.GUEST, label: 'Guest Event', emoji: '🥂' },
  { id: EVENTS.FOOD, label: 'Food & Dining', emoji: '🍽️' },
  { id: EVENTS.VOYAGE, label: 'Voyage', emoji: '⛵' },
  // ponytail: no trip entity any more. Rows saved as 'trip' keep that type and read as a voyage.
  { id: EVENTS.TRIP, label: 'Voyage', emoji: '⛵' },
];

export type EventStatus = 'planned' | 'underway' | 'completed' | 'canceled';

export type EventCalendar = {
  id: UUID;
  name: string;
};

export type EventLocation = {
  id: UUID;
  name: string;
  address: string;
  latitude: number;
  longitude: number;
  map_url: string;
};

export type EventTimeSlot = {
  id: UUID;
  type: 'day_based' | string;
  start: ISODateString;
  end: ISODateString;
  all_day?: boolean;
};

export enum EventParticipantType {
  GUEST = 'guest',
  CREW = 'crew',
  SERVICE_PROVIDER = 'service_provider',
}

export type EventParticipant = {
  person_id: UUID;
  type: EventParticipantType;
  status?: string;
  decline_reason?: string;
  name?: string;
  /** Guests the participant brings along; guests only. */
  additional_guests?: number;
};

/** The people one participant stands for: themselves and the guests they bring. */
export const headcount = (x: { additional_guests?: number }) => 1 + (x.additional_guests ?? 0);

/** {@link headcount} for an invitation or an answer, which carry the count camelCased. */
export const partyHeadcount = (x: { additionalGuests?: number }) => 1 + (x.additionalGuests ?? 0);

export const sumHeadcount = <T>(xs: T[], count: (x: T) => number) =>
  xs.reduce((sum, x) => sum + count(x), 0);

/** " +2" to follow a name, or nothing when they come alone. */
export const extraGuestsTag = (n?: number) => (n ? ` +${n}` : '');

/** "1 guest", "3 guests". */
export const guestsLabel = (n: number) => `${n} ${n === 1 ? 'guest' : 'guests'}`;

export const getParticipantStatus = (
  participants: EventParticipant[],
  userId: string,
): string | undefined => participants?.find((p) => p?.person_id === userId)?.status;

export const getParticipantDeclineReason = (
  participants: EventParticipant[],
  userId: string,
): string | undefined => participants.find((p) => p.person_id === userId)?.decline_reason;

export type EventAttachment = {
  id: UUID;
  name: string;
  link: string;
  content_type: string;
  size: number;
  /** Video and audio only — see {@link MediaStatus}. */
  media_status?: MediaStatus;
  variants?: Record<string, MediaVariantDto>;
};

export type SubActivity = {
  id: UUID;
  title: string;
  description: string;
  time_slot: {
    start: ISODateString;
    end: ISODateString;
  };
  participants: EventParticipant[];
  temporary_chat_id: string | null;
  has_menu?: boolean;
};

export type SubEventInput = Omit<SubActivity, 'id' | 'temporary_chat_id' | 'has_menu'> & {
  id?: string;
};

export type EventResponse = {
  id: UUID;
  generator_id: UUID | null;
  vessel_id: UUID;
  calendar: EventCalendar;
  participants: EventParticipant[];
  title: string;
  description: string;
  location: EventLocation;
  created_at: ISODateString;
  updated_at: ISODateString;
  type: EVENTS;
  status?: EventStatus;
  time_slot: EventTimeSlot | null;
  comments_count: number;
  parent: SubActivity | null;
  sub_activities: SubActivity[];
  image: FileDto | null;
  attachments: EventAttachment[];
  /** The event chat room. Null on an event that never got one. */
  temp_chat_id: string | null;
  has_menu?: boolean;
  /** Read through `eventTags`. Set by people or by the AI tagger after a save. */
  tags?: TagDto[];
};

export type DatedEvent = EventResponse & { time_slot: EventTimeSlot };

export const isDated = (e: EventResponse): e is DatedEvent => e.time_slot !== null;

/** An event saved without dates: planned, not on the calendar yet. */
export const isIdeaEvent = (e: EventResponse): boolean =>
  e.status === 'planned' && e.time_slot === null;

/** What the event wizard stamps on a location nobody filled in. */
export const UNSPECIFIED_LOCATION = 'Unspecified';

/**
 * The place an event ends up, or null while that is still a gap. An event packs its
 * ordered stops into `location.address` — see `parseStops` — and `location.name`
 * only holds the first of them, which is where it leaves from, not where it is
 * going. The destination is the last stop that is not the departure port, so a
 * round trip (Miami → Bimini → Miami) reads Bimini, not Miami.
 *
 * ponytail: last stop away from home, not the farthest — Miami → Bimini → Nassau →
 * Bimini → Miami reads Bimini. Farthest needs coordinates, which this does not have.
 */
export const eventDestination = (e: EventResponse): string | null => {
  const stops = parseStops(e.location?.address, e.location?.name).filter(
    (name) => name !== UNSPECIFIED_LOCATION,
  );
  const away = stops.filter((name) => name !== stops[0]);
  return (away[away.length - 1] ?? stops[0])?.trim() || null;
};

export const guestParticipants = (e: EventResponse): EventParticipant[] =>
  (e.participants ?? []).filter((p) => p.type === EventParticipantType.GUEST);

/**
 * RSVP split over the participants an event carries, in people: each guest counts with the
 * guests they bring. A guest who has not answered is not a participant at all — that count
 * comes from the event's open invites, via `pendingGuestInvites`.
 */
export const rsvpCounts = (e: EventResponse) => {
  const guests = guestParticipants(e);
  let confirmed = 0;
  let declined = 0;
  for (const g of guests) {
    if (g.status === 'accepted') confirmed += headcount(g);
    else if (g.status === 'declined') declined += headcount(g);
  }
  return { total: sumHeadcount(guests, headcount), confirmed, declined };
};

export type DerivedStatus =
  | { label: 'Idea'; tone: 'neutral' }
  | { label: 'Canceled'; tone: 'danger' }
  | { label: 'Planned'; tone: 'info' }
  | { label: 'Underway'; tone: 'warning' }
  | { label: 'Completed'; tone: 'success' };

export const deriveEventStatus = (e: EventResponse): DerivedStatus | null => {
  if (e.status === 'canceled') return { label: 'Canceled', tone: 'danger' };
  if (isIdeaEvent(e)) return { label: 'Idea', tone: 'neutral' };
  switch (e.status) {
    case 'planned':
      return { label: 'Planned', tone: 'info' };
    case 'underway':
      return { label: 'Underway', tone: 'warning' };
    case 'completed':
      return { label: 'Completed', tone: 'success' };
    default:
      return null;
  }
};

export type GalleryImage = {
  id: string;
  event_id: string;
  name: string;
  file_id: string;
  link: string;
  content_type: string;
  size: number;
  created_at: ISODateString;
  updated_at: ISODateString;
  /** Videos only — see {@link MediaStatus}. */
  media_status?: MediaStatus;
  /** Absent when there are none; activity links each one. */
  variants?: Record<string, MediaVariantDto>;
};

export type EventsListResponse = {
  items: EventResponse[];
  count: number;
};

export type CalendarEvent = {
  id: UUID;
  title: string;
  start: ISODateString;
  end: ISODateString;
  allDay: boolean;
  borderColor: string;
  extendedProps: EventResponse | TaskResponse;
};

/** activity GET /events/guest-suggestions — people who were guests at this vessel's past events. */
export interface GuestSuggestionDto {
  person_id: string;
  name: string;
  events_count: number;
  accepted_count: number;
  last_event_at: string;
  last_event_title: string;
  last_additional_guests?: number;
}

export interface GuestSuggestionsResponseDto {
  suggestions: GuestSuggestionDto[];
}
